<script setup lang="ts">
import { Check, Copy, Mail, Phone, PhoneCall, User } from 'lucide-vue-next'
import { computed } from 'vue'

import AppTooltip from '@/components/AppTooltip.vue'
import CallTracker from '@/components/applications/CallTracker.vue'
import GeneratePsa from '@/components/applications/GeneratePsa.vue'
import ReinstateApplication from '@/components/applications/ReinstateApplication.vue'
import RejectApplication from '@/components/applications/RejectApplication.vue'
import RequestAdditionalDocuments from '@/components/applications/RequestAdditionalDocuments.vue'
import SettleApplication from '@/components/applications/SettleApplication.vue'
import StatusBadge from '@/components/applications/StatusBadge.vue'
import VerifyIdentity from '@/components/applications/VerifyIdentity.vue'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { Separator } from '@/components/ui/separator'
import { useCopyToClipboard } from '@/composables/useCopyToClipboard'
import { getAvatarColor, useInitials } from '@/composables/useInitials'
import { formatAmount, formatDateTime, formatLabel } from '@/lib/utils'

interface Props {
  application: {
    id: string
    status: string
    status_at: string | null
    rejection_reason: string | null
    rejected_on: string | null
    loan_type: string
    purpose: string
    amount: number
    loan_term: number | null
    call_count: number
    cr_score: number | null
    one_score: number | null
    id_matrix_status: string | null
    psa_generated_at: string | null
    customer_name: string
    partner_name: string
    customer: {
      email: string
      phone: string
      nickname: string | null
    }
  }
  rejectionReasons: Record<string, string>
  optionalDocNames: string[]
  existingDocNames: string[]
}

const props = defineProps<Props>()

const { copy, isCopied } = useCopyToClipboard()
const { getInitials } = useInitials()

const customerInitials = computed(() => getInitials(props.application.customer_name))
const customerAvatarColor = computed(() => getAvatarColor(props.application.customer_name))
</script>

<template>
  <div class="space-y-4">
    <!-- Contact card -->
    <Card class="gap-2">
      <CardHeader class="pb-1">
        <CardTitle class="text-sm font-medium">Contact</CardTitle>
      </CardHeader>
      <CardContent class="space-y-2.5 pt-0">
        <ContextMenu>
          <ContextMenuTrigger as-child>
            <button
              type="button"
              class="group/avatar mb-4 flex w-full justify-center"
              :title="isCopied('app-id') ? 'Copied!' : 'Click to copy application ID'"
              @click="copy(application.id, 'app-id')"
            >
              <Avatar class="size-16 transition-opacity group-hover/avatar:opacity-75">
                <AvatarFallback class="text-lg font-semibold" :class="customerAvatarColor">
                  <Check v-if="isCopied('app-id')" class="size-5" />
                  <template v-else>{{ customerInitials }}</template>
                </AvatarFallback>
              </Avatar>
            </button>
          </ContextMenuTrigger>
          <ContextMenuContent class="w-52">
            <ContextMenuItem @click="copy(application.id, 'app-id')">
              <Copy class="size-4" />
              Copy application ID
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem @click="copy(application.customer.email, 'email')">
              <Mail class="size-4" />
              Copy email
            </ContextMenuItem>
            <ContextMenuItem @click="copy(application.customer.phone, 'phone')">
              <Phone class="size-4" />
              Copy phone
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem as="a" :href="`tel:${application.customer.phone}`">
              <PhoneCall class="size-4" />
              Call {{ application.customer.phone }}
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
        <div class="flex items-center gap-2.5">
          <User class="text-muted-foreground size-4 shrink-0" />
          <span class="text-sm font-medium">{{ application.customer_name }}</span>
          <span v-if="application.customer.nickname" class="text-sm font-light">({{ application.customer.nickname }})</span>
        </div>
        <div class="flex items-center gap-2.5">
          <Mail class="text-muted-foreground size-4 shrink-0" />
          <span class="text-muted-foreground text-sm">{{ application.customer.email }}</span>
        </div>
        <div class="flex items-center gap-2.5">
          <Phone class="text-muted-foreground size-4 shrink-0" />
          <a :href="`tel:${application.customer.phone}`" class="text-muted-foreground text-sm hover:underline">
            {{ application.customer.phone }}
          </a>
        </div>
      </CardContent>
    </Card>

    <!-- Application overview -->
    <Card class="gap-2">
      <CardHeader class="pb-3">
        <CardTitle class="text-sm font-medium">Overview</CardTitle>
      </CardHeader>
      <CardContent class="pt-0">
        <dl class="space-y-2.5 text-sm">
          <div class="flex items-center justify-between gap-2">
            <dt class="text-muted-foreground shrink-0">Status</dt>
            <dd><StatusBadge :status="application.status" :status-at="application.status_at" :rejection-reason="application.rejection_reason" /></dd>
          </div>

          <Separator />

          <div class="flex items-center justify-between gap-2">
            <dt class="text-muted-foreground shrink-0">Partner</dt>
            <dd class="text-right font-medium">{{ application.partner_name }}</dd>
          </div>
          <div class="flex items-center justify-between gap-2">
            <dt class="text-muted-foreground shrink-0">Type</dt>
            <dd class="font-medium">{{ formatLabel(application.loan_type) }}</dd>
          </div>
          <div class="flex items-center justify-between gap-2">
            <dt class="text-muted-foreground shrink-0">Purpose</dt>
            <dd class="text-right font-medium">{{ formatLabel(application.purpose) }}</dd>
          </div>
          <div class="flex items-center justify-between gap-2">
            <dt class="text-muted-foreground shrink-0">Amount</dt>
            <dd class="font-medium">{{ formatAmount(application.amount) }}</dd>
          </div>
          <div v-if="application.loan_term" class="flex items-center justify-between gap-2">
            <dt class="text-muted-foreground shrink-0">Term</dt>
            <dd class="font-medium">{{ application.loan_term }} months</dd>
          </div>

          <Separator />

          <div class="flex items-center justify-between gap-2">
            <dt class="text-muted-foreground shrink-0">CR Score</dt>
            <dd class="font-medium">{{ application.cr_score ?? '—' }}</dd>
          </div>
          <div class="flex items-center justify-between gap-2">
            <dt class="text-muted-foreground shrink-0">ONE Score</dt>
            <dd class="font-medium">{{ application.one_score ?? '—' }}</dd>
          </div>
          <div class="flex items-center justify-between gap-2">
            <dt class="text-muted-foreground shrink-0">ID Verified</dt>
            <dd class="font-medium">{{ application.id_matrix_status ?? '—' }}</dd>
          </div>
          <div class="flex items-center justify-between gap-2">
            <dt class="text-muted-foreground shrink-0">PSA</dt>
            <dd>
              <AppTooltip v-if="application.psa_generated_at" side="top">
                <Badge variant="outline" class="border-green-500 text-green-600">Generated</Badge>
                <template #content>{{ formatDateTime(application.psa_generated_at) }}</template>
              </AppTooltip>
              <span v-else class="font-medium">—</span>
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>

    <!-- Reinstate action — only shown for rejected applications -->
    <Card v-if="application.status === 'rejected' && application.rejected_on" class="gap-2">
      <CardHeader class="pb-3">
        <CardTitle class="text-sm font-medium">Actions</CardTitle>
      </CardHeader>
      <CardContent class="pt-0">
        <ReinstateApplication
          :application-id="application.id"
          :rejected-on="application.rejected_on!"
        />
      </CardContent>
    </Card>

    <!-- Actions — hidden once rejected or settled -->
    <Card class="gap-2" v-if="!['rejected', 'settled'].includes(application.status)">
      <CardHeader class="pb-3">
        <CardTitle class="text-sm font-medium">Actions</CardTitle>
      </CardHeader>
      <CardContent class="space-y-4 pt-0">
        <CallTracker
          className="mb-4 w-full border border-green-600 rounded-lg p-4 hover:bg-green-100"
          v-if="application.status === 'new'"
          :application-id="application.id"
          :call-count="application.call_count"
        />
        <GeneratePsa
          v-if="application.status === 'accepted' && !application.psa_generated_at"
          :application-id="application.id"
        />
        <RequestAdditionalDocuments
          v-if="application.status !== 'new'"
          :application-id="application.id"
          :optional-doc-names="optionalDocNames"
          :existing-doc-names="existingDocNames"
        />
        <SettleApplication v-if="application.status === 'approved'" :application-id="application.id" />
        <VerifyIdentity :application-id="application.id" />
        <RejectApplication :application-id="application.id" :reasons="rejectionReasons" />
      </CardContent>
    </Card>
  </div>
</template>
